import { trim } from 'lodash-es'
import { Account } from 'types'

import { graphql } from '@/graphql'
import { getTokens, setTokens } from '@/auth/tokenStore'
import { removeAuthCookie, getAuthCookie } from 'src/utils/cookies'
import { Client } from 'urql'
import { postLogout } from '@/features/auth/utils/authBroadcast'
import { classifyLoginFailure } from '@/features/auth/utils/loginFailure'
import { isProjectHubRole } from 'src/utils/projectHubRole'

export interface AuthResult {
  success: boolean
  redirectTo?: string
  logout?: boolean
  error?: { message: string; name?: string }
}

export interface CheckResult {
  authenticated: boolean
  error?: { message: string; name?: string; statusCode?: number }
  logout?: boolean
  redirectTo?: string
}

export interface OnErrorResult {
  error?: { message: string; statusCode?: number }
  logout?: boolean
  redirectTo?: string
}

export interface AuthService {
  login: (params: { email: string; password: string }) => Promise<AuthResult>
  logout: (params?: unknown) => Promise<AuthResult>
  onError: (error: { statusCode?: number } | null) => Promise<OnErrorResult>
  check: () => Promise<CheckResult>
  getPermissions: () => Promise<string | null>
  getIdentity: () => Promise<Account | null>
}

export const createConditionalAuthProvider = (
  client: Client,
  shouldDisableAuth: boolean = false,
): AuthService => {
  if (shouldDisableAuth) {
    return {
      login: async () => ({ success: true, redirectTo: '/' }),
      logout: async () => ({ success: true, redirectTo: '/' }),
      onError: async () => ({ error: undefined }),
      check: async () => ({ authenticated: true }),
      getPermissions: async () => null,
      getIdentity: async () => null,
    }
  }

  return authProvider(client)
}

export const authProvider = (client: Client): AuthService => ({
  login: async ({ email, password }) => {
    const query = graphql(`
      mutation Login($email: String!, $password: String!) {
        Login(email: $email, password: $password) {
          access_token
          access_token_expiry
          account_type
          email
          id
          last_connected
          refresh_token
          refresh_token_expiry
          session_started_at
        }
      }
    `)
    setTokens(null)
    try {
      const result = await client
        .mutation(query, { email: trim(email), password }, {
          fetchOptions: {
            headers: {
              'x-hasura-role': 'public',
            },
          },
        })
        .toPromise()

      if (result.error) {
        throw result.error
      }

      const data = result.data?.Login as Account

      if (!data || !data.access_token || !data.refresh_token) {
        throw new Error('Invalid login response')
      }

      const loginData = data as Account & {
        access_token_expiry: string
        session_started_at: string
      }
      setTokens({
        access_token: loginData.access_token,
        access_token_expiry: Date.parse(loginData.access_token_expiry),
        refresh_token: loginData.refresh_token,
        refresh_token_expiry: Date.parse(loginData.refresh_token_expiry as unknown as string),
        session_started_at: Date.parse(loginData.session_started_at),
        account_type: loginData.account_type,
        email: loginData.email,
        id: loginData.id,
      })

      return {
        success: true,
        // Salesmen and companies don't have a dashboard — they land directly
        // on the project hub. Other roles keep the architect default.
        redirectTo: isProjectHubRole(loginData.account_type)
          ? '/projects'
          : '/dashboard',
      }
    } catch (error) {
      const failure = classifyLoginFailure(error)
      console.error(`Login error [${failure.kind}]:`, error)
      return {
        logout: true,
        success: false,
        redirectTo: '/login',
        // `name` IS the classification — the page reads it to choose what to
        // say. It is never absent, so the page never has to fall back to
        // guessing that the password was wrong.
        error: { name: failure.kind, message: failure.detail ?? '' },
      }
    }
  },
  logout: async () => {
    try {
      // Send refresh token (not access token) so the backend can find and
      // revoke it in the refresh_tokens table. Uses fetch directly to bypass
      // urql's authExchange which would attach the access token instead.
      const tokens = getTokens()
      if (tokens?.refresh_token) {
        await fetch('/v1/graphql', {
          method: 'POST',
          headers: {
            'content-type': 'application/json',
            Authorization: `Bearer ${tokens.refresh_token}`,
          },
          body: JSON.stringify({
            query: 'mutation Logout { Logout { success } }',
          }),
        })
      }
    } catch {
      // Best-effort — always clear local state even if server call fails
    }
    removeAuthCookie()
    setTokens(null)
    postLogout()
    return {
      success: true,
      redirectTo: '/login',
    }
  },
  onError: async (error) => {
    if (error && error.statusCode === 401) {
      postLogout('expired')
      return {
        error: {
          message: 'Unauthorized',
          statusCode: 401,
        },
        logout: true,
        redirectTo: '/login?reason=expired',
      }
    }
    return {
      error: {
        message: 'Error',
        statusCode: 500,
      },
    }
  },
  check: async () => {
    const authCookie = getAuthCookie()
    try {
      const parsed = JSON.parse(authCookie || '') as {
        account_type?: string
        access_token?: string
        refresh_token?: string
        refresh_token_expiry?: number | string
        session_started_at?: number | string
      }
      const now = Date.now()
      const refreshExpiry = typeof parsed.refresh_token_expiry === 'number'
        ? parsed.refresh_token_expiry
        : Date.parse(parsed.refresh_token_expiry ?? '')
      const sessionStarted = typeof parsed.session_started_at === 'number'
        ? parsed.session_started_at
        : Date.parse(parsed.session_started_at ?? '')
      const absoluteEnd = sessionStarted
        ? sessionStarted + 30 * 24 * 60 * 60 * 1000
        : Number.POSITIVE_INFINITY

      if (
        parsed?.account_type &&
        parsed?.access_token &&
        parsed?.refresh_token &&
        Number.isFinite(refreshExpiry) &&
        now < refreshExpiry &&
        now < absoluteEnd
      ) {
        return {
          authenticated: true,
        }
      }
    } catch {
      removeAuthCookie()
    }
    return {
      authenticated: false,
      error: {
        message: 'Check failed',
        name: 'Unauthorized',
        statusCode: 401,
      },
      logout: true,
      redirectTo: '/login?reason=expired',
    }
  },
  getPermissions: async () => {
    try {
      const authCookie = getAuthCookie()
      if (authCookie) {
        const parsedUser = JSON.parse(authCookie)
        return parsedUser.account_type
      }
    } catch {}
    return null
  },
  getIdentity: async () => {
    const authCookie = getAuthCookie()
    if (authCookie) {
      const parsedUser = JSON.parse(authCookie)
      return parsedUser
    }
    return null
  },
})
